import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapesjs-preset-webpage';
import { ArrowLeft, Save, Globe, Eye, Code, Smartphone, Monitor, ExternalLink, Shield, FileText } from 'lucide-react';
import sitesApi from '../../services/sitesApi';
import { useToast } from '../../context/ToastContext';
import { useSiteAuth } from '../../context/SiteAuthContext';

export default function SiteEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { siteUser } = useSiteAuth();
    const editorRef = useRef(null);
    const [editor, setEditor] = useState(null);
    const [site, setSite] = useState(null);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [showPublishSuccessUrl, setShowPublishSuccessUrl] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [showPublishConfigModal, setShowPublishConfigModal] = useState(false);
    const [publishSlug, setPublishSlug] = useState('');
    const [publishCustomDomain, setPublishCustomDomain] = useState('');
    const [showPoliciesModal, setShowPoliciesModal] = useState(false);
    const [privacyPolicy, setPrivacyPolicy] = useState('');
    const [termsConditions, setTermsConditions] = useState('');

    useEffect(() => {
        if (!id) return;
        
        const loadSiteAndEditor = async () => {
            try {
                const res = await sitesApi.getSite(id);
                const siteData = res.data.site || res.data;
                setSite(siteData);
                setPublishSlug(siteData.slug || '');
                setPublishCustomDomain(siteData.custom_domain || '');
                setPrivacyPolicy(siteData.privacy_policy || '');
                setTermsConditions(siteData.terms_conditions || '');
                initEditor(siteData);
            } catch (err) {
                console.error('Failed to load site', err);
                toast.error('Failed to load site data.');
                navigate('/sites/dashboard');
            }
        };
        
        loadSiteAndEditor();

        return () => {
            if (editor) {
                editor.destroy();
            }
        };
    }, [id, navigate]);

    const initEditor = (siteData) => {
        if (editor) return;

        const e = grapesjs.init({
            container: editorRef.current,
            height: '100%',
            width: '100%',
            storageManager: false,
            plugins: ['gjs-preset-webpage'],
            pluginsOpts: {
                'gjs-preset-webpage': {
                    // options
                }
            },
            assetManager: {
                uploadFile: async (ev) => {
                    const files = ev.dataTransfer ? ev.dataTransfer.files : ev.target.files;
                    if (!files || files.length === 0) return;
                    const file = files[0];
                    const formData = new FormData();
                    formData.append('image', file);
                    if (id) {
                        formData.append('siteId', id);
                    }
                    
                    setUploadingImage(true);
                    try {
                        const res = await sitesApi.post('/api/sites/upload', formData, {
                            headers: {
                                'Content-Type': undefined
                            }
                        });
                        
                        if (res.data && res.data.secureUrl) {
                            // Add the asset to GrapesJS asset manager
                            e.Assets.add([{
                                src: res.data.secureUrl,
                                type: 'image'
                            }]);
                            // Set the active component target to the new URL if editing one
                            const selected = e.getSelected();
                            if (selected) {
                                if (selected.is('image')) {
                                    selected.set('src', res.data.secureUrl);
                                } else {
                                    selected.addStyle({ 'background-image': `url("${res.data.secureUrl}")` });
                                }
                            }
                            e.Modal.close();
                        }
                    } catch (err) {
                        const errMsg = err.response?.data?.error || err.response?.data?.message || err.message;
                        console.error('Failed to upload site image to Cloudinary:', errMsg, err);
                        alert(`Failed to upload image: ${errMsg}`);
                    } finally {
                        setUploadingImage(false);
                    }
                }
            },
            canvas: {
                styles: [
                    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
                    'https://cdn.tailwindcss.com'
                ]
            }
        });

        // Smart double-click handler for Hero Sections and Images
        e.on('component:doubleclick', (comp) => {
            if (!comp) return;

            // 1. Direct image component
            if (comp.is('image')) {
                e.runCommand('open-assets', { target: comp });
                return;
            }

            // 2. Component containing an <img> tag (e.g. Hero Section block)
            const imgChild = comp.find('img')[0];
            if (imgChild) {
                e.runCommand('open-assets', { target: imgChild });
                return;
            }

            // 3. Search up parent tree for background-image style or <img> child
            let curr = comp.parent();
            while (curr && curr.get('tagName') !== 'body') {
                const foundImg = curr.find('img')[0];
                if (foundImg) {
                    e.runCommand('open-assets', { target: foundImg });
                    return;
                }
                
                const style = curr.getStyle();
                if (style && style['background-image']) {
                    e.runCommand('open-assets', {
                        target: curr,
                        onSelect(asset) {
                            const src = typeof asset === 'string' ? asset : asset.getSrc();
                            curr.addStyle({ 'background-image': `url("${src}")` });
                            e.Modal.close();
                        }
                    });
                    return;
                }
                curr = curr.parent();
            }
        });

        // Register Custom Blocks
        const bm = e.BlockManager;

        bm.add('hero-section', {
            label: 'Hero Section',
            category: 'Restaurant',
            content: `
                <section style="position: relative; padding: 120px 20px; text-align: center; color: white; overflow: hidden; min-height: 450px; display: flex; align-items: center; justify-content: center; width: 100%;">
                    <!-- Double-clickable Hero Background Image -->
                    <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200" alt="Hero Background Image" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; z-index: 0;" />
                    
                    <!-- Absolute Background Overlay (Customize background color/alpha to adjust dark overlay) -->
                    <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.55); z-index: 1; pointer-events: none;"></div>
                    
                    <!-- Opaque Text Content Container -->
                    <div style="position: relative; z-index: 2; max-width: 800px; margin: 0 auto;">
                        <h1 style="font-size: 3.5rem; margin-bottom: 20px; font-weight: 700; font-family: 'Inter', sans-serif;">Welcome to Our Restaurant</h1>
                        <p style="font-size: 1.25rem; margin-bottom: 30px; font-family: 'Inter', sans-serif;">Fresh ingredients, craft recipes, and memorable culinary moments.</p>
                        <a href="#menu" style="display: inline-block; padding: 12px 30px; background-color: #f97316; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; font-family: 'Inter', sans-serif; transition: background 0.3s;">Explore Menu</a>
                    </div>
                </section>
            `
        });

        bm.add('menu-section', {
            label: 'Menu Grid',
            category: 'Restaurant',
            content: `
                <section style="padding: 60px 20px; width: 100%; font-family: 'Inter', sans-serif;">
                    <div style="max-width: 1200px; margin: 0 auto;">
                        <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem; font-weight: 700;">Our Menu</h2>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 30px;">
                            <div style="border: 1px solid #eee; padding: 20px; border-radius: 12px; background: white; box-shadow: 0 4px 6px rgba(0,0,0,0.02); transition: transform 0.2s;">
                                <!-- Clickable Image (Double click in editor to change image URL or upload) -->
                                <img src="https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400" alt="Margherita Pizza" style="width: 100%; height: 180px; object-fit: cover; border-radius: 8px; margin-bottom: 15px;" />
                                <h3 style="margin: 0 0 10px 0; font-size: 1.25rem; font-weight: 600;">Margherita Pizza</h3>
                                <p style="color: #666; margin-bottom: 15px; font-size: 0.9rem; line-height: 1.4;">Fresh tomatoes, sliced mozzarella, sweet basil, and extra virgin olive oil.</p>
                                <div style="font-weight: bold; color: #f97316; font-size: 1.15rem;">$12.99</div>
                            </div>
                            <div style="border: 1px solid #eee; padding: 20px; border-radius: 12px; background: white; box-shadow: 0 4px 6px rgba(0,0,0,0.02); transition: transform 0.2s;">
                                <!-- Clickable Image (Double click in editor to change image URL or upload) -->
                                <img src="https://images.unsplash.com/photo-1612874742237-6526221588e3?w=400" alt="Pasta Carbonara" style="width: 100%; height: 180px; object-fit: cover; border-radius: 8px; margin-bottom: 15px;" />
                                <h3 style="margin: 0 0 10px 0; font-size: 1.25rem; font-weight: 600;">Pasta Carbonara</h3>
                                <p style="color: #666; margin-bottom: 15px; font-size: 0.9rem; line-height: 1.4;">Creamy egg and parmesan sauce, crispy pancetta, and freshly cracked black pepper.</p>
                                <div style="font-weight: bold; color: #f97316; font-size: 1.15rem;">$14.99</div>
                            </div>
                        </div>
                    </div>
                </section>
            `
        });

        bm.add('header-section', {
            label: 'Responsive Header',
            category: 'Restaurant',
            content: `
                <header style="border-bottom: 1px solid #eee; position: sticky; top: 0; z-index: 999; font-family: 'Inter', sans-serif; box-shadow: 0 2px 4px rgba(0,0,0,0.02); width: 100%;">
                    <div style="max-width: 1200px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; position: relative;">
                        <!-- Brand Logo & Name Container -->
                        <div style="display: flex; align-items: center; gap: 12px;">
                            <!-- Double-clickable Logo (Double click in editor to change image URL or upload) -->
                            <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=100" alt="Restaurant Logo" style="height: 40px; width: 40px; object-fit: cover; border-radius: 8px; flex-shrink: 0;" />
                            <div style="font-size: 1.5rem; font-weight: bold; color: #1e293b; font-family: 'Inter', sans-serif;">My Restaurant</div>
                        </div>
                        
                        <!-- Pure CSS Hamburger Toggle -->
                        <input type="checkbox" id="nav-toggle" style="display: none;" />
                        <label for="nav-toggle" class="hamburger" style="display: none; cursor: pointer; flex-direction: column; gap: 5px; z-index: 1000;">
                            <span style="width: 25px; height: 3px; background: #334155; border-radius: 2px;"></span>
                            <span style="width: 25px; height: 3px; background: #334155; border-radius: 2px;"></span>
                            <span style="width: 25px; height: 3px; background: #334155; border-radius: 2px;"></span>
                        </label>
                        
                        <nav class="nav-links" style="display: flex; gap: 24px; align-items: center;">
                            <a href="#" onclick="var c=document.getElementById('nav-toggle'); if(c) c.checked=false;" style="text-decoration: none; color: #64748b; font-weight: 600; font-size: 0.95rem; transition: color 0.2s;">Home</a>
                            <a href="#menu" onclick="var c=document.getElementById('nav-toggle'); if(c) c.checked=false;" style="text-decoration: none; color: #64748b; font-weight: 600; font-size: 0.95rem; transition: color 0.2s;">Menu</a>
                            <a href="#gallery" onclick="var c=document.getElementById('nav-toggle'); if(c) c.checked=false;" style="text-decoration: none; color: #64748b; font-weight: 600; font-size: 0.95rem; transition: color 0.2s;">Gallery</a>
                            <a href="#about" onclick="var c=document.getElementById('nav-toggle'); if(c) c.checked=false;" style="text-decoration: none; color: #64748b; font-weight: 600; font-size: 0.95rem; transition: color 0.2s;">About</a>
                            <a href="#contact" onclick="var c=document.getElementById('nav-toggle'); if(c) c.checked=false;" style="text-decoration: none; color: #64748b; font-weight: 600; font-size: 0.95rem; transition: color 0.2s;">Contact</a>
                        </nav>
                    </div>
                    <style>
                        @media (max-width: 768px) {
                            .hamburger { display: flex !important; }
                            .nav-links {
                                display: none !important;
                                flex-direction: column;
                                width: 100%;
                                background: white;
                                position: absolute;
                                top: 100%;
                                left: 0;
                                border-top: 1px solid #e2e8f0;
                                padding: 15px 0;
                                box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
                                z-index: 998;
                            }
                            .nav-links a {
                                padding: 12px 24px;
                                width: 100%;
                                box-sizing: border-box;
                            }
                            #nav-toggle:checked ~ .nav-links {
                                display: flex !important;
                            }
                        }
                    </style>
                </header>
            `
        });

        bm.add('gallery-section', {
            label: 'Gallery Grid',
            category: 'Restaurant',
            content: `
                <section id="gallery" style="padding: 60px 20px; width: 100%; font-family: 'Inter', sans-serif;">
                    <div style="max-width: 1200px; margin: 0 auto;">
                        <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem; font-weight: 700; color: #1e293b;">Our Gallery</h2>
                        <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px;">
                            <div style="border-radius: 12px; overflow: hidden; height: 240px; box-shadow: 0 4px 6px rgba(0,0,0,0.03); border: 1px solid #f1f5f9;">
                                <img src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600" alt="Special Dish 1" style="width: 100%; height: 100%; object-fit: cover;" />
                            </div>
                            <div style="border-radius: 12px; overflow: hidden; height: 240px; box-shadow: 0 4px 6px rgba(0,0,0,0.03); border: 1px solid #f1f5f9;">
                                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600" alt="Special Dish 2" style="width: 100%; height: 100%; object-fit: cover;" />
                            </div>
                            <div style="border-radius: 12px; overflow: hidden; height: 240px; box-shadow: 0 4px 6px rgba(0,0,0,0.03); border: 1px solid #f1f5f9;">
                                <img src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600" alt="Special Dish 3" style="width: 100%; height: 100%; object-fit: cover;" />
                            </div>
                            <div style="border-radius: 12px; overflow: hidden; height: 240px; box-shadow: 0 4px 6px rgba(0,0,0,0.03); border: 1px solid #f1f5f9;">
                                <img src="https://images.unsplash.com/photo-1482049016688-2d3e1b311543?w=600" alt="Special Dish 4" style="width: 100%; height: 100%; object-fit: cover;" />
                            </div>
                        </div>
                    </div>
                </section>
            `
        });

        bm.add('about-section', {
            label: 'About Us',
            category: 'Restaurant',
            content: `
                <section id="about" style="padding: 80px 20px; width: 100%; font-family: 'Inter', sans-serif; background-color: #ffffff;">
                    <div style="max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 50px; align-items: center;">
                        <div style="border-radius: 16px; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); height: 380px;">
                            <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800" alt="About Our Restaurant" style="width: 100%; height: 100%; object-fit: cover;" />
                        </div>
                        <div>
                            <span style="color: #f97316; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; font-size: 0.875rem;">Our Story</span>
                            <h2 style="font-size: 2.5rem; font-weight: 800; color: #0f172a; margin: 10px 0 20px 0; line-height: 1.2;">Passionate About Authentic Flavors</h2>
                            <p style="color: #475569; font-size: 1.05rem; line-height: 1.7; margin-bottom: 20px;">
                                Founded with a mission to bring craft recipes and artisanal preparation to your table, our kitchen selects only local organic produce and prime ingredients daily.
                            </p>
                            <p style="color: #475569; font-size: 1.05rem; line-height: 1.7; margin-bottom: 30px;">
                                Whether dining with family or hosting a private event, every meal is prepared with care, passion, and culinary perfection.
                            </p>
                            <div style="display: flex; gap: 30px;">
                                <div>
                                    <div style="font-size: 2rem; font-weight: 800; color: #f97316;">15+</div>
                                    <div style="color: #64748b; font-size: 0.9rem;">Years Experience</div>
                                </div>
                                <div>
                                    <div style="font-size: 2rem; font-weight: 800; color: #f97316;">50k+</div>
                                    <div style="color: #64748b; font-size: 0.9rem;">Happy Guests</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            `
        });

        bm.add('contact-section', {
            label: 'Contact & Form',
            category: 'Restaurant',
            content: `
                <section id="contact" style="padding: 80px 20px; width: 100%; font-family: 'Inter', sans-serif; background-color: #f8fafc;">
                    <div style="max-width: 1200px; margin: 0 auto;">
                        <div style="text-align: center; margin-bottom: 50px;">
                            <span style="color: #f97316; font-weight: 700; text-transform: uppercase; font-size: 0.875rem;">Get In Touch</span>
                            <h2 style="font-size: 2.5rem; font-weight: 800; color: #0f172a; margin-top: 8px;">Contact & Reservations</h2>
                        </div>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 40px;">
                            <!-- Contact Details Card -->
                            <div style="background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #e2e8f0;">
                                <h3 style="font-size: 1.5rem; font-weight: 700; color: #0f172a; margin-bottom: 25px;">Location & Hours</h3>
                                
                                <div style="margin-bottom: 20px; display: flex; gap: 15px; align-items: flex-start;">
                                    <div style="background: #fff7ed; padding: 10px; border-radius: 8px; color: #f97316; font-weight: bold;">📍</div>
                                    <div>
                                        <div style="font-weight: 600; color: #1e293b;">Address</div>
                                        <div style="color: #64748b; font-size: 0.95rem;">123 Gourmet Boulevard, Foodville, NY 10001</div>
                                    </div>
                                </div>
                                
                                <div style="margin-bottom: 20px; display: flex; gap: 15px; align-items: flex-start;">
                                    <div style="background: #fff7ed; padding: 10px; border-radius: 8px; color: #f97316; font-weight: bold;">📞</div>
                                    <div>
                                        <div style="font-weight: 600; color: #1e293b;">Phone</div>
                                        <div style="color: #64748b; font-size: 0.95rem;">+1 (555) 234-5678</div>
                                    </div>
                                </div>
                                
                                <div style="margin-bottom: 20px; display: flex; gap: 15px; align-items: flex-start;">
                                    <div style="background: #fff7ed; padding: 10px; border-radius: 8px; color: #f97316; font-weight: bold;">✉️</div>
                                    <div>
                                        <div style="font-weight: 600; color: #1e293b;">Email</div>
                                        <div style="color: #64748b; font-size: 0.95rem;">hello@ourrestaurant.com</div>
                                    </div>
                                </div>

                                <div style="display: flex; gap: 15px; align-items: flex-start;">
                                    <div style="background: #fff7ed; padding: 10px; border-radius: 8px; color: #f97316; font-weight: bold;">🕒</div>
                                    <div>
                                        <div style="font-weight: 600; color: #1e293b;">Opening Hours</div>
                                        <div style="color: #64748b; font-size: 0.95rem;">Mon - Sun: 11:00 AM - 10:30 PM</div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Reservation / Message Form -->
                            <div style="background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #e2e8f0;">
                                <h3 style="font-size: 1.5rem; font-weight: 700; color: #0f172a; margin-bottom: 25px;">Send a Message</h3>
                                <form style="display: flex; flex-direction: column; gap: 20px;">
                                    <div>
                                        <label style="display: block; font-size: 0.875rem; font-weight: 600; color: #334155; margin-bottom: 6px;">Your Name</label>
                                        <input type="text" placeholder="John Doe" required style="width: 100%; padding: 12px 16px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; outline: none; box-sizing: border-box;" />
                                    </div>
                                    <div>
                                        <label style="display: block; font-size: 0.875rem; font-weight: 600; color: #334155; margin-bottom: 6px;">Email Address</label>
                                        <input type="email" placeholder="john@example.com" required style="width: 100%; padding: 12px 16px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; outline: none; box-sizing: border-box;" />
                                    </div>
                                    <div>
                                        <label style="display: block; font-size: 0.875rem; font-weight: 600; color: #334155; margin-bottom: 6px;">Message or Special Request</label>
                                        <textarea rows="4" placeholder="How can we help you?" required style="width: 100%; padding: 12px 16px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 0.95rem; outline: none; box-sizing: border-box; resize: vertical;"></textarea>
                                    </div>
                                    <button type="submit" style="padding: 14px 28px; background-color: #f97316; color: white; border: none; border-radius: 8px; font-weight: 600; font-size: 1rem; cursor: pointer; transition: background 0.2s;">Send Message</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            `
        });

        bm.add('footer-section', {
            label: 'Footer & Social Links',
            category: 'Restaurant',
            content: `
                <footer style="background-color: #0f172a; color: #94a3b8; padding: 60px 20px 30px 20px; font-family: 'Inter', sans-serif; width: 100%;">
                    <div style="max-width: 1200px; margin: 0 auto;">
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 40px; margin-bottom: 50px;">
                            <!-- Brand Column -->
                            <div>
                                <h3 style="color: white; font-size: 1.5rem; font-weight: 700; margin-bottom: 15px;">My Restaurant</h3>
                                <p style="color: #94a3b8; font-size: 0.95rem; line-height: 1.6; margin-bottom: 20px;">
                                    Serving authentic flavors, fresh ingredients, and memorable dining experiences daily.
                                </p>
                                <!-- Social Media Links -->
                                <div style="display: flex; gap: 12px;">
                                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" style="width: 38px; height: 38px; border-radius: 50%; background: #1e293b; color: white; display: flex; align-items: center; justify-content: center; text-decoration: none; font-size: 1rem; transition: background 0.2s;" title="Facebook">
                                        <span>f</span>
                                    </a>
                                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" style="width: 38px; height: 38px; border-radius: 50%; background: #1e293b; color: white; display: flex; align-items: center; justify-content: center; text-decoration: none; font-size: 1rem; transition: background 0.2s;" title="Instagram">
                                        <span>📷</span>
                                    </a>
                                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" style="width: 38px; height: 38px; border-radius: 50%; background: #1e293b; color: white; display: flex; align-items: center; justify-content: center; text-decoration: none; font-size: 1rem; transition: background 0.2s;" title="Twitter">
                                        <span>🐦</span>
                                    </a>
                                    <a href="https://tripadvisor.com" target="_blank" rel="noopener noreferrer" style="width: 38px; height: 38px; border-radius: 50%; background: #1e293b; color: white; display: flex; align-items: center; justify-content: center; text-decoration: none; font-size: 1rem; transition: background 0.2s;" title="TripAdvisor">
                                        <span>⭐</span>
                                    </a>
                                </div>
                            </div>

                            <!-- Quick Navigation -->
                            <div>
                                <h4 style="color: white; font-size: 1.1rem; font-weight: 600; margin-bottom: 20px;">Quick Links</h4>
                                <ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px;">
                                    <li><a href="#" style="color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: color 0.2s;">Home</a></li>
                                    <li><a href="#menu" style="color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: color 0.2s;">Our Menu</a></li>
                                    <li><a href="#gallery" style="color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: color 0.2s;">Gallery</a></li>
                                    <li><a href="#about" style="color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: color 0.2s;">About Us</a></li>
                                    <li><a href="#contact" style="color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: color 0.2s;">Contact Us</a></li>
                                </ul>
                            </div>

                            <!-- Operating Hours -->
                            <div>
                                <h4 style="color: white; font-size: 1.1rem; font-weight: 600; margin-bottom: 20px;">Hours & Service</h4>
                                <div style="font-size: 0.95rem; line-height: 1.8;">
                                    <div><strong style="color: #e2e8f0;">Monday - Friday:</strong></div>
                                    <div style="margin-bottom: 8px;">11:00 AM - 10:00 PM</div>
                                    <div><strong style="color: #e2e8f0;">Saturday - Sunday:</strong></div>
                                    <div>10:00 AM - 11:00 PM</div>
                                </div>
                            </div>

                            <!-- Newsletter / Contact Snippet -->
                            <div>
                                <h4 style="color: white; font-size: 1.1rem; font-weight: 600; margin-bottom: 20px;">Newsletter</h4>
                                <p style="font-size: 0.9rem; margin-bottom: 15px;">Subscribe to get special offers & chef secrets.</p>
                                <form style="display: flex; gap: 8px;">
                                    <input type="email" placeholder="Your email" required style="padding: 10px 14px; border-radius: 6px; border: 1px solid #334155; background: #1e293b; color: white; font-size: 0.9rem; outline: none; width: 100%; box-sizing: border-box;" />
                                    <button type="submit" style="padding: 10px 16px; background-color: #f97316; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer;">Join</button>
                                </form>
                            </div>
                        </div>

                        <!-- Bottom Bar / Copyright -->
                        <div style="border-top: 1px solid #1e293b; padding-top: 25px; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 15px; font-size: 0.875rem;">
                            <div>© 2026 My Restaurant. All rights reserved.</div>
                            <div style="display: flex; gap: 20px;">
                                <a href="#privacy-policy" style="color: #64748b; text-decoration: none;">Privacy Policy</a>
                                <a href="#terms-of-service" style="color: #64748b; text-decoration: none;">Terms of Service</a>
                            </div>
                        </div>
                    </div>
                </footer>
            `
        });

        // Load data if exists
        if (siteData.page_data) {
            try {
                const projectData = typeof siteData.page_data === 'string'
                    ? JSON.parse(siteData.page_data)
                    : siteData.page_data;
                e.loadProjectData(projectData);
            } catch (err) {
                console.error("Error parsing page data:", err);
            }
        }

        setEditor(e);
    };

    const handlePreviewExternal = () => {
        if (!site) return;
        if (!site.is_published) {
            toast.info('This website is currently in draft. Please click "Publish" in the top-right toolbar to compile and view your site live.');
            return;
        }
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const baseDomain = isLocal ? `localhost:${window.location.port || '5173'}` : 'aaharamcloud.com';
        const protocol = window.location.protocol;
        
        const siteUrl = `${protocol}//${site.slug}.${baseDomain}`;
        window.open(siteUrl, '_blank');
    };

    const handleSave = async () => {
        if (!editor || !site) return;
        setSaving(true);
        try {
            const projectData = editor.getProjectData();
            await sitesApi.updateSite(site.id || site._id, {
                page_data: JSON.stringify(projectData),
                privacy_policy: privacyPolicy,
                terms_conditions: termsConditions
            });
            toast.success('Changes saved successfully!');
        } catch (err) {
            console.error('Save error', err);
            toast.error('Failed to save changes.');
        } finally {
            setSaving(false);
        }
    };

    const handleSavePolicies = async () => {
        if (!site) return;
        setSaving(true);
        try {
            await sitesApi.updateSite(site.id || site._id, {
                privacy_policy: privacyPolicy,
                terms_conditions: termsConditions
            });
            setSite(prev => ({
                ...prev,
                privacy_policy: privacyPolicy,
                terms_conditions: termsConditions
            }));
            setShowPoliciesModal(false);
            toast.success('Policies saved successfully!');
        } catch (err) {
            console.error('Failed to save policies', err);
            toast.error('Failed to save policies.');
        } finally {
            setSaving(false);
        }
    };

    const submitPublish = async () => {
        if (!editor || !site) return;
        setPublishing(true);
        try {
            // Update site configurations first (slug & custom domain if not Free)
            await sitesApi.updateSettings(site.id || site._id, {
                slug: publishSlug,
                custom_domain: siteUser?.plan_name !== 'Free' ? publishCustomDomain : undefined
            });

            // Compile page structure
            const html = editor.getHtml();
            const css = editor.getCss();
            await sitesApi.publishSite(site.id || site._id, {
                html_content: html,
                css_content: css
            });

            // Sync settings state
            setSite(prev => ({
                ...prev,
                slug: publishSlug,
                custom_domain: siteUser?.plan_name !== 'Free' ? publishCustomDomain : prev.custom_domain,
                is_published: true
            }));

            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const baseDomain = isLocal ? `localhost:${window.location.port || '5173'}` : 'aaharamcloud.com';
            const protocol = window.location.protocol;
            const domainToUse = siteUser?.plan_name !== 'Free' && publishCustomDomain 
                ? publishCustomDomain 
                : `${publishSlug}.${baseDomain}`;
            const siteUrl = `${protocol}//${domainToUse}`;
            
            setShowPublishConfigModal(false);
            setShowPublishSuccessUrl(siteUrl);
            toast.success('Site published successfully!');
        } catch (err) {
            console.error('Publish error', err);
            const errMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to publish site.';
            toast.error(errMsg);
        } finally {
            setPublishing(false);
        }
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-slate-900">
            {/* Custom Toolbar */}
            <div className="h-14 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/sites/dashboard')} className="text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="text-white font-medium">{site?.name || 'Loading...'}</div>
                </div>
                
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => editor && editor.runCommand('set-device-desktop')}
                        className="p-1.5 text-slate-400 hover:text-white rounded" title="Desktop View"
                    >
                        <Monitor size={18} />
                    </button>
                    <button 
                        onClick={() => editor && editor.runCommand('set-device-mobile')}
                        className="p-1.5 text-slate-400 hover:text-white rounded" title="Mobile View"
                    >
                        <Smartphone size={18} />
                    </button>
                    <button 
                        onClick={handlePreviewExternal}
                        className="p-1.5 text-slate-400 hover:text-white rounded ml-2 border-l border-slate-800 pl-4" title="Preview Live Site"
                    >
                        <Eye size={18} />
                    </button>
                    
                    <div className="h-6 w-px bg-slate-800 mx-2"></div>
                    
                    <button
                        onClick={() => setShowPoliciesModal(true)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-md text-sm font-medium transition-colors"
                        title="Edit Privacy Policy & Terms"
                    >
                        <Shield size={16} />
                        Policies
                    </button>

                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                    >
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={() => setShowPublishConfigModal(true)}
                        disabled={publishing}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 ml-1"
                    >
                        <Globe size={16} />
                        {publishing ? 'Publishing...' : 'Publish'}
                    </button>
                </div>
            </div>

            {/* GrapesJS Container */}
            <div className="flex-1 relative">
                <div ref={editorRef} className="h-full w-full"></div>
            </div>
            
            {/* Custom CSS to theme GrapesJS dark */}
            <style dangerouslySetInnerHTML={{__html: `
                .gjs-one-bg { background-color: #0f172a; }
                .gjs-two-color { color: #cbd5e1; }
                .gjs-three-bg { background-color: #1e293b; color: white; }
                .gjs-four-color, .gjs-four-color-h:hover { color: #f97316; }
                .gjs-pn-panel { background-color: #0f172a; border-color: #1e293b; }
                .gjs-cv-canvas { background-color: #cbd5e1; }
                .gjs-block { background-color: #1e293b; border: 1px solid #334155; color: #cbd5e1; border-radius: 8px; }
                .gjs-block:hover { color: #f97316; border-color: #f97316; }
            `}} />

            {/* Publication Success Modal */}
            {showPublishSuccessUrl && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-md shadow-2xl text-center animate-scale-in">
                        <div className="mx-auto w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
                            <Globe size={32} className="text-emerald-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Congratulations!</h3>
                        <p className="text-slate-400 text-sm mb-6">Your website has been successfully published and is now live on the internet!</p>
                        
                        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 mb-6 flex items-center justify-between">
                            <a 
                                href={showPublishSuccessUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="text-emerald-400 hover:text-emerald-300 font-medium text-sm truncate flex items-center gap-1.5"
                            >
                                {showPublishSuccessUrl.replace(/^https?:\/\//, '')}
                                <ExternalLink size={14} />
                            </a>
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(showPublishSuccessUrl);
                                    toast.success('URL copied to clipboard!');
                                }}
                                className="text-xs bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-700 transition-colors"
                            >
                                Copy
                            </button>
                        </div>
                        
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowPublishSuccessUrl(null)}
                                className="flex-1 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors border border-slate-800"
                            >
                                Done
                            </button>
                            <a
                                href={showPublishSuccessUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-center rounded-xl font-medium transition-colors flex items-center justify-center gap-1.5"
                            >
                                Visit Site
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Publish Configuration Modal */}
            {showPublishConfigModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-md shadow-2xl text-left animate-scale-in">
                        <div className="flex items-center gap-2.5 mb-6 border-b border-slate-800 pb-4">
                            <Globe className="text-primary-500" size={24} />
                            <h3 className="text-2xl font-bold text-white">Publish Settings</h3>
                        </div>
                        
                        <div className="space-y-6">
                            {/* Subdomain slug */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Website Subdomain (Slug)</label>
                                <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-950">
                                    <input 
                                        type="text" 
                                        required
                                        className="flex-1 bg-transparent px-4 py-2.5 text-white focus:outline-none text-sm"
                                        value={publishSlug}
                                        onChange={(e) => setPublishSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    />
                                    <span className="bg-slate-800 text-slate-400 px-3.5 py-2.5 text-xs select-none border-l border-slate-700 flex items-center font-mono">
                                        .{window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `localhost:${window.location.port || '5173'}` : 'aaharamcloud.com'}
                                    </span>
                                </div>
                                <p className="mt-1.5 text-xs text-slate-500">Only lowercase alphanumeric characters and hyphens allowed.</p>
                            </div>

                            {/* Custom Domain (Pro-only) */}
                            <div className="border border-slate-800 rounded-2xl p-5 bg-slate-950/30 relative">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-bold text-slate-300 flex items-center gap-2">
                                        <Globe size={16} className="text-primary-500" /> Custom Domain
                                    </span>
                                    {siteUser?.plan_name === 'Free' && (
                                        <span className="bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                            PAID Feature
                                        </span>
                                    )}
                                    {siteUser?.plan_name === 'Basic' && (
                                        <span className="bg-primary-500/10 text-primary-400 border border-primary-500/20 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                                            BASIC (1 Domain)
                                        </span>
                                    )}
                                    {siteUser?.plan_name === 'Pro' && (
                                        <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider font-mono">
                                            PRO (Unlimited)
                                        </span>
                                    )}
                                </div>

                                {siteUser?.plan_name !== 'Free' ? (
                                    <div>
                                        <input 
                                            type="text"
                                            className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-primary-500 text-sm"
                                            placeholder="e.g. www.myrestaurant.com"
                                            value={publishCustomDomain}
                                            onChange={(e) => setPublishCustomDomain(e.target.value.trim().toLowerCase())}
                                        />
                                        {publishCustomDomain && (
                                            <div className="mt-3 bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-[11px] text-slate-400 leading-relaxed font-mono">
                                                <strong>DNS configuration:</strong> Add CNAME record pointing <strong>{publishCustomDomain}</strong> to <strong>sites.aaharamcloud.com</strong>
                                            </div>
                                        )}
                                        {siteUser?.plan_name === 'Basic' && (
                                            <p className="mt-2 text-[10px] text-slate-500">Your Basic plan supports connecting 1 custom domain across your sites.</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-2">
                                        <p className="text-xs text-slate-500 mb-3">Custom domain mapping is not supported on the Free plan.</p>
                                        <button 
                                            type="button"
                                            onClick={() => navigate('/sites/subscription')}
                                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors"
                                        >
                                            Upgrade Plan
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                type="button"
                                onClick={() => setShowPublishConfigModal(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors border border-slate-800 text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitPublish}
                                disabled={publishing || !publishSlug}
                                className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm"
                            >
                                {publishing ? 'Publishing...' : 'Publish Site'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Policy & Terms Modal */}
            {showPoliciesModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-2xl shadow-2xl text-left animate-scale-in flex flex-col max-h-[85vh]">
                        <div className="flex items-center gap-2.5 mb-6 border-b border-slate-800 pb-4">
                            <Shield className="text-primary-500" size={24} />
                            <h3 className="text-2xl font-bold text-white">Legal Policies</h3>
                        </div>
                        
                        <div className="space-y-6 overflow-y-auto pr-2 flex-1">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Privacy Policy Content</label>
                                <textarea
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-500 text-sm h-40 font-sans"
                                    placeholder="Write or paste your privacy policy here..."
                                    value={privacyPolicy}
                                    onChange={(e) => setPrivacyPolicy(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Terms of Service / Terms & Conditions Content</label>
                                <textarea
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-500 text-sm h-40 font-sans"
                                    placeholder="Write or paste your terms of service here..."
                                    value={termsConditions}
                                    onChange={(e) => setTermsConditions(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6 border-t border-slate-800 pt-6">
                            <button
                                type="button"
                                onClick={() => setShowPoliciesModal(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors border border-slate-800 text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSavePolicies}
                                disabled={saving}
                                className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 text-sm"
                            >
                                {saving ? 'Saving...' : 'Save Policies'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Uploading Overlay */}
            {uploadingImage && (
                <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-slate-950/60 backdrop-blur-sm">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mb-4"></div>
                    <div className="text-white font-medium text-lg animate-pulse">Uploading Image to Cloudinary...</div>
                </div>
            )}
        </div>
    );
}
